import { SET_FILTER_DEBOUNCE_MS } from "../constants/grid";

/**
 * Get default fiscal year filter model
 * 
 * This function determines the default fiscal year(s) to filter based on the current date.
 * If the date is after August 15th, it includes both the current year and next year.
 * Otherwise, it only includes the current year.
 * 
 * @returns {Object} Filter model object with fiscalYear filter
 * @example
 * // Returns: { fiscalYear: { type: "equals", filter: "2025" } }
 * // or: { fiscalYear: { operator: "OR", conditions: [{ type: "equals", filter: "2025" }, { type: "equals", filter: "2026" }] } }
 */
export const getDefaultFiscalYearFilterModel = () => {
  const currentDateObj = new Date();
  const currentYear = currentDateObj.getFullYear();
  const currentMonth = currentDateObj.getMonth(); // 0-based (0 = Jan, 7 = Aug)
  const currentDate = currentDateObj.getDate();

  // Determine default fiscal years
  let defaultFiscalYears = [currentYear.toString()];

  // If date is after 15th August, include next year as well
  if (currentMonth > 7 || (currentMonth === 7 && currentDate > 15)) {
    defaultFiscalYears.push((currentYear + 1).toString());
  }

  // Build filter model
  return {
    fiscalYear:
      defaultFiscalYears.length > 1
        ? { operator: "OR", conditions: defaultFiscalYears.map(y => ({ type: "equals", filter: y })) }
        : { type: "equals", filter: defaultFiscalYears[0] },
  };
};

/**
 * Shared AG Grid Set Filter (checkbox filter) behaviour defaults.
 *
 * `excelMode: 'mac'` makes the in-dropdown search (mini filter) drive the
 * "(Select All)" checkbox: with a search term active, "(Select All)" toggles
 * only the currently matched (visible) values instead of the whole list. Without
 * it, "(Select All)" acts on the entire value set and ignores the search
 * (TPX-1907). We use 'mac' rather than 'windows' because 'windows' adds an
 * "Add current selection to filter" checkbox that tested as confusing.
 *
 * 'mac' mode has no Apply button and applies on interaction. To stop rapid
 * multi-value selection from firing one server search per checkbox on the
 * SERVER-SIDE Plans grid, a shared `debounceMs` ({@link SET_FILTER_DEBOUNCE_MS})
 * coalesces consecutive toggles into a single filter-changed event. On CLIENT-SIDE
 * grids this simply debounces local filtering (no server call). This was chosen
 * over an Apply button (kept the applies-on-interaction UX) and over 'windows'
 * mode's confusing "Add current selection" checkbox.
 *
 * No `defaultToNothingSelected` — the filter starts all-selected so the grid
 * shows all data on first load. These are behaviour-only params — no
 * `values`/`valueFormatter`/`cellRenderer` — so they are safe to reuse on every
 * set-filter column and on `defaultColDef`.
 *
 * RULE FOR NEW GRIDS/COLUMNS:
 * - Build every `agSetColumnFilter` column's `filterParams` via
 *   `makeSetFilterParams({ ...columnSpecific })`.
 * - Also spread `SET_FILTER_DEFAULTS` into the grid's `defaultColDef.filterParams`
 *   so a future column that omits its own `filterParams` still inherits the
 *   shared params (AG Grid does NOT deep-merge — a column's own `filterParams`
 *   fully replaces `defaultColDef.filterParams`, which is why the per-column
 *   helper is required, not just the default).
 */
export const SET_FILTER_DEFAULTS = {
  excelMode: "mac",
  suppressSorting: true,
  buttons: ["reset"],
  filterPlaceholder: "Search",
  // Coalesce rapid checkbox toggles into one filter-changed event (one server search on SSRM).
  debounceMs: SET_FILTER_DEBOUNCE_MS,
};

/**
 * Build `filterParams` for an `agSetColumnFilter` column, layering the shared
 * defaults ({@link SET_FILTER_DEFAULTS}) under any column-specific overrides
 * (e.g. `values`, `valueFormatter`, `cellRenderer`, `debounceMs`).
 *
 * @param {Object} [overrides={}] Column-specific set-filter params.
 * @returns {Object} Merged filterParams.
 * @example
 * filterParams: makeSetFilterParams({ values: ["Budget", "Forecast"] })
 */
export const makeSetFilterParams = (overrides = {}) => ({
  ...SET_FILTER_DEFAULTS,
  ...overrides,
});

